import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { UpgradeNudge } from "../components/FeatureGate";
import {
  FileText, ArrowLeft, Loader2, Trash2, IndianRupee, Plus, Download,
  CheckCircle2, Calendar, Building2, CreditCard, Clock, Upload, Paperclip, Eye, X, File, Share2, Copy, Shield, ExternalLink,
  PenTool, ShieldCheck, AlertTriangle
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import CreditNoteDialog from "../components/CreditNoteDialog";
import { formatIndianDate, docNouns as deriveDocNouns, paymentTermsLabel } from "../utils/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  unpaid: { bg: "bg-red-50 border-red-100", text: "text-red-700", label: "Unpaid" },
  partial: { bg: "bg-amber-50 border-amber-100", text: "text-amber-700", label: "Partial" },
  paid: { bg: "bg-emerald-50 border-emerald-100", text: "text-emerald-700", label: "Paid" },
};

export default function BuilderInvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [paymentMode, setPaymentMode] = useState("");
  const [savingPayment, setSavingPayment] = useState(false);
  const [evidence, setEvidence] = useState([]);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [managedBy, setManagedBy] = useState(null);
  const [signing, setSigning] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [editStatus, setEditStatus] = useState({ editable: true, reason: "" });
  const [showCreditNoteDialog, setShowCreditNoteDialog] = useState(false);

  useEffect(() => { fetchInvoice(); fetchEvidence(); fetchManagedBy(); fetchEditStatus(); }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      const res = await axios.get(`${API}/builder/invoices/detail/${invoiceId}`, { withCredentials: true });
      setInvoice(res.data);
    } catch (err) {
      toast.error("Failed to load invoice");
      navigate("/invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchEditStatus = async () => {
    try {
      const res = await axios.get(`${API}/builder/invoices/${invoiceId}/edit-status`, { withCredentials: true });
      setEditStatus(res.data);
    } catch { /* ignore */ }
  };

  const handleCreditNoteCreated = (creditNote) => {
    setShowCreditNoteDialog(false);
    if (creditNote?.credit_note_id) {
      navigate(`/credit-notes/${creditNote.credit_note_id}`);
    } else {
      navigate("/credit-notes");
    }
  };

  const fetchEvidence = async () => {
    try {
      const res = await axios.get(`${API}/evidence/list/${invoiceId}`, { withCredentials: true });
      setEvidence(res.data.evidence || []);
    } catch { /* ignore */ }
  };

  const fetchManagedBy = async () => {
    try {
      const res = await axios.get(`${API}/ca/my-ca`, { withCredentials: true });
      if (res.data.has_ca) setManagedBy(res.data.ca);
    } catch { /* ignore */ }
  };

  const handleSign = async () => {
    if (!window.confirm("Digitally sign this invoice? Once signed, any modification will invalidate the signature.")) return;
    setSigning(true);
    try {
      await axios.post(`${API}/builder/invoices/${invoiceId}/sign`, {}, { withCredentials: true });
      toast.success("Invoice digitally signed!");
      fetchInvoice();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signing failed");
    } finally {
      setSigning(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const res = await axios.get(`${API}/builder/invoices/${invoiceId}/verify-signature`, { withCredentials: true });
      setVerifyResult(res.data);
    } catch {
      toast.error("Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const handleRevokeSignature = async () => {
    if (!window.confirm("Revoke signature? This will remove the digital sign from this invoice.")) return;
    try {
      await axios.post(`${API}/builder/invoices/${invoiceId}/revoke-signature`, {}, { withCredentials: true });
      toast.success("Signature revoked");
      setVerifyResult(null);
      fetchInvoice();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleShareInvoice = async () => {
    setSharing(true);
    try {
      const res = await axios.post(`${API}/share/generate/${invoiceId}`, {}, { withCredentials: true });
      const fullUrl = `${window.location.origin}/claim/${res.data.share_token}`;
      setShareUrl(fullUrl);
      try { await navigator.clipboard.writeText(fullUrl); toast.success("Share link copied!"); }
      catch { toast.success("Share link generated!"); }
    } catch (err) {
      toast.error("Failed to generate share link");
    } finally {
      setSharing(false);
    }
  };

  const handleEvidenceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max file size is 10MB"); return; }
    setUploadingEvidence(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/evidence/upload/${invoiceId}`, fd, { withCredentials: true });
      toast.success("Evidence uploaded");
      fetchEvidence();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingEvidence(false);
      e.target.value = "";
    }
  };

  const handleDeleteEvidence = async (evidenceId) => {
    if (!window.confirm("Delete this evidence file?")) return;
    try {
      await axios.delete(`${API}/evidence/${evidenceId}`, { withCredentials: true });
      toast.success("Evidence deleted");
      fetchEvidence();
    } catch {
      toast.error("Failed to delete evidence");
    }
  };

  const handleAddPayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    setSavingPayment(true);
    try {
      await axios.post(`${API}/builder/payments`, {
        invoice_id: invoiceId,
        amount,
        payment_date: paymentDate,
        payment_mode: paymentMode,
      }, { withCredentials: true });
      toast.success("Payment recorded!");
      setShowPaymentForm(false);
      setPaymentAmount("");
      setPaymentMode("");
      fetchInvoice();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payment");
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Remove this payment?")) return;
    try {
      await axios.delete(`${API}/builder/payments/delete/${paymentId}`, { withCredentials: true });
      toast.success("Payment removed");
      fetchInvoice();
    } catch (err) {
      toast.error("Failed to remove payment");
    }
  };

  const fmt = (a) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(a || 0);

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!invoice) return null;

  const status = STATUS_STYLES[invoice.payment_status] || STATUS_STYLES.unpaid;
  const totalPaid = (invoice.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
  const remaining = (invoice.grand_total || 0) - totalPaid;
  const company = invoice.company;

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="invoice-detail-page">
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate("/invoices")} className="mb-4 text-slate-600" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Invoices
        </Button>

        {/* Action Bar */}
        <div className="flex flex-wrap justify-end gap-2 mb-4">
          {/* Sign / Verify */}
          {invoice.digital_signature ? (
            <>
              <Button onClick={handleVerify} disabled={verifying} variant="outline" className="text-sm border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="verify-signature-btn">
                {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />} Verify Signature
              </Button>
              <Button onClick={handleRevokeSignature} variant="outline" className="text-sm border-red-200 text-red-600 hover:bg-red-50" data-testid="revoke-signature-btn">
                <X className="w-4 h-4 mr-1" /> Revoke Sign
              </Button>
            </>
          ) : (
            <Button onClick={handleSign} disabled={signing} variant="outline" className="text-sm border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="sign-invoice-btn">
              {signing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PenTool className="w-4 h-4 mr-1" />} Sign Invoice
            </Button>
          )}
          <Button
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/builder/invoices/${invoiceId}/pdf`, {
                  withCredentials: true,
                  responseType: "blob",
                });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const link = document.createElement("a");
                link.href = url;
                link.download = `${(invoice?.invoice_number || "invoice").replace(/\//g, "-")}.pdf`;
                link.click();
                window.URL.revokeObjectURL(url);
                // GA4: activation event — actual invoice usage
                try { window.gtag?.("event", "first_invoice_downloaded", { invoice_id: invoiceId }); } catch { /* ignore */ }
              } catch {
                toast.error("Failed to download PDF");
              }
            }}
            variant="outline"
            className="text-sm"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4 mr-1" /> Download PDF
          </Button>
          <Button onClick={handleShareInvoice} disabled={sharing} variant="outline" className="text-sm" data-testid="share-invoice-btn">
            {sharing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Share2 className="w-4 h-4 mr-1" />} Share
          </Button>
          {invoice.invoice_type === "sales" && !editStatus.editable && editStatus.suggest_credit_note && (
            <Button
              onClick={() => setShowCreditNoteDialog(true)}
              variant="outline"
              className="text-sm border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid="issue-credit-note-btn"
            >
              <FileText className="w-4 h-4 mr-1" /> Issue Credit Note
            </Button>
          )}
        </div>

        {/* Deleted-invoice notice (audit) */}
        {invoice.deleted && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2 text-sm" data-testid="deleted-notice">
            <Trash2 className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-red-800">
              <p className="font-semibold">This invoice has been deleted.</p>
              <p className="text-xs mt-0.5">Kept for audit only — number <span className="font-mono">{invoice.invoice_number}</span> is reserved and cannot be re-issued. Edits, payments, sharing and signing are disabled.{invoice.deleted_at ? ` Deleted on ${new Date(invoice.deleted_at).toLocaleString("en-IN")}.` : ""}</p>
            </div>
          </div>
        )}

        {/* Edit-lock notice */}
        {invoice.invoice_type === "sales" && !editStatus.editable && editStatus.reason && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-start gap-2 text-sm" data-testid="edit-lock-notice">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-amber-800">{editStatus.reason}</p>
          </div>
        )}

        {/* Share URL */}
        {shareUrl && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-4 flex items-center gap-2" data-testid="share-url-bar">
            <span className="text-xs text-blue-700 font-mono truncate flex-1">{shareUrl}</span>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied!"); }} className="text-xs text-blue-600">
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
          </div>
        )}

        {/* Invoice Card */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-8 mb-6" data-testid="invoice-card">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-6 sm:mb-8">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {invoice.invoice_type === "sales"
                    ? deriveDocNouns(company).title
                    : (deriveDocNouns(company).isBill ? "PURCHASE BILL" : "PURCHASE INVOICE")}
                </h1>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${status.bg} ${status.text}`}>
                  {status.label}
                </span>
              </div>
              <p className="text-xs text-slate-500 font-medium"><span className="font-semibold text-slate-700">{deriveDocNouns(company).numberLabel}:</span> <span className="font-mono break-all">{invoice.invoice_number}</span></p>
              {Array.isArray(invoice.source_quotation_ids) && invoice.source_quotation_ids.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="linked-quotations">
                  {invoice.source_quotation_ids.map((qid) => (
                    <Link
                      key={qid}
                      to={`/quotations/${qid}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border border-blue-200 bg-blue-50 text-blue-700 px-2 py-0.5 hover:bg-blue-100"
                      data-testid={`linked-quote-${qid}`}
                    >
                      Linked Quote <span className="font-mono">·</span> <span className="font-mono">{qid.slice(-6)}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="text-left sm:text-right text-sm text-slate-600 shrink-0">
              <p><span className="font-medium">{deriveDocNouns(company).dateLabel}:</span> {formatIndianDate(invoice.invoice_date)}</p>
              {invoice.due_date && <p><span className="font-medium">Due:</span> {formatIndianDate(invoice.due_date)}</p>}
              {(() => {
                const pt = paymentTermsLabel(invoice.payment_terms, invoice.due_date, company?.payment_terms_days);
                return pt ? <p className="text-xs text-blue-700 font-semibold mt-1" data-testid="payment-terms-text">{pt}</p> : null;
              })()}
            </div>
          </div>

          {/* From / To */}
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            {company && (
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">From</p>
                {company.logo_url && (
                  <img
                    src={company.logo_url.startsWith("http") ? company.logo_url : `${process.env.REACT_APP_BACKEND_URL}${company.logo_url}`}
                    alt={`${company.company_name} logo`}
                    className="h-14 w-auto max-w-[140px] object-contain mb-2"
                    data-testid="company-logo"
                  />
                )}
                <p className="font-semibold text-slate-900">{company.company_name}</p>
                {company.address && <p className="text-sm text-slate-600">{company.address}</p>}
                {company.gst_number && <p className="text-sm text-slate-600">GST: {company.gst_number}</p>}
              </div>
            )}
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Bill To</p>
              <p className="font-semibold text-slate-900">{invoice.customer_name}</p>
              {invoice.customer_address && <p className="text-sm text-slate-600">{invoice.customer_address}</p>}
              {invoice.customer_gst && <p className="text-sm text-slate-600">GST: {invoice.customer_gst}</p>}
            </div>
          </div>

          {/* Managed By */}
          {managedBy && (
            <div className="flex items-center gap-2 mb-6 p-3 bg-violet-50 rounded-xl border border-violet-100" data-testid="managed-by-invoice">
              <Shield className="w-4 h-4 text-violet-500 flex-shrink-0" />
              <p className="text-xs text-violet-700">
                Managed by <span className="font-semibold">{managedBy.name}</span>
                {managedBy.firm_name && <span> ({managedBy.firm_name})</span>}
                {managedBy.professional_id && (
                  <>
                    {" | "}
                    <Link to={`/pro/${managedBy.professional_id}`} className="font-mono font-semibold text-violet-800 hover:underline" data-testid="managed-by-profile-link">
                      {managedBy.professional_id} <ExternalLink className="w-3 h-3 inline" />
                    </Link>
                  </>
                )}
              </p>
            </div>
          )}

          {/* Branding Upgrade Nudge */}
          <UpgradeNudge feature="no_branding" message="Remove InvoiceSetu branding from your invoices — upgrade now" compact={false} />

          {/* Digital Signature Status */}
          {invoice.digital_signature && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="signature-seal">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-emerald-800">Digitally Signed</p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    Signed by <span className="font-semibold">{invoice.digital_signature.signer_name}</span>
                    {invoice.digital_signature.signer_org && <span> ({invoice.digital_signature.signer_org})</span>}
                    {" on "}
                    {new Date(invoice.digital_signature.signed_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                  <p className="text-[10px] text-emerald-500 font-mono mt-1">
                    SHA-256: {invoice.digital_signature.invoice_hash?.slice(0, 24)}...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Verification Result */}
          {verifyResult && (
            <div className={`mb-6 p-4 rounded-xl border ${verifyResult.valid ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="verify-result">
              <div className="flex items-start gap-3">
                {verifyResult.valid ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className={`text-sm font-bold ${verifyResult.valid ? "text-emerald-800" : "text-red-800"}`}>
                    {verifyResult.valid ? "Signature Valid" : "Signature Invalid"}
                  </p>
                  <p className={`text-xs mt-0.5 ${verifyResult.valid ? "text-emerald-600" : "text-red-600"}`}>
                    {verifyResult.message}
                  </p>
                  {verifyResult.signer_name && (
                    <p className="text-xs text-slate-500 mt-1">
                      Signer: {verifyResult.signer_name}
                      {verifyResult.signer_org && ` (${verifyResult.signer_org})`}
                      {" | Signed: "}
                      {verifyResult.signed_at ? new Date(verifyResult.signed_at).toLocaleDateString("en-IN") : "N/A"}
                    </p>
                  )}
                </div>
              </div>
              <button onClick={() => setVerifyResult(null)} className="text-xs text-slate-400 hover:text-slate-600 mt-2">Dismiss</button>
            </div>
          )}

          {/* Items Table — table on ≥sm, card list on mobile */}
          {(() => {
            const allItems = invoice.items || [];
            const servicesOnly = allItems.length > 0 && allItems.every(it => (it.kind || "product") === "service");
            return (
            <>
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm mb-6">
              <thead>
                <tr className="border-b-2 border-slate-200 text-slate-500">
                  <th className="text-left py-2 font-medium">#</th>
                  <th className="text-left py-2 font-medium">Item</th>
                  <th className="text-left py-2 font-medium">{servicesOnly ? "SAC" : "HSN/SAC"}</th>
                  {!servicesOnly && <th className="text-right py-2 font-medium">Qty</th>}
                  <th className="text-right py-2 font-medium">{servicesOnly ? "Amount" : "Rate"}</th>
                  <th className="text-right py-2 font-medium">Taxable</th>
                  <th className="text-right py-2 font-medium">GST</th>
                  <th className="text-right py-2 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.items || []).map((item, i) => {
                  const qty = Number(item.quantity) || 0;
                  const rate = Number(item.unit_price) || 0;
                  const base = qty * rate;
                  const taxable = Number(item.taxable_amount ?? base) || 0;
                  const totalDiscount = Number(item.discount_amount ?? 0) || 0;
                  const lineDisc = Number(item.line_discount ?? 0) || 0;
                  const overallShare = Math.max(0, totalDiscount - lineDisc);
                  const itemTax = Number(item.tax_amount ?? 0) || 0;
                  return (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="py-2.5 text-slate-400">{i + 1}</td>
                      <td className="py-2.5">
                        <span className="font-medium text-slate-900">{item.name}</span>
                        {item.description && <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>}
                        {totalDiscount > 0.01 && (
                          <p className="text-[10px] text-red-500 mt-0.5">
                            Gross {fmt(base)}
                            {lineDisc > 0.01 && <> · Line -{fmt(lineDisc)}</>}
                            {overallShare > 0.01 && <> · Overall -{fmt(overallShare)}</>}
                          </p>
                        )}
                      </td>
                      <td className="py-2.5 text-slate-600 font-mono text-xs">{item.hsn_code || "—"}</td>
                      {!servicesOnly && (
                        <td className="py-2.5 text-right text-slate-600">{(item.kind || "product") === "service" ? "—" : `${qty}${item.unit ? ` ${item.unit}` : ""}`}</td>
                      )}
                      <td className="py-2.5 text-right text-slate-600">{fmt(rate)}</td>
                      <td className="py-2.5 text-right text-slate-700">{fmt(taxable)}</td>
                      <td className="py-2.5 text-right text-slate-600">
                        {item.gst_not_applicable ? "—" : <>{fmt(itemTax)}<span className="text-[10px] text-slate-400 ml-1">({item.gst_percent || 0}%)</span></>}
                      </td>
                      <td className="py-2.5 text-right font-medium text-slate-900">{fmt(item.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile line-item cards */}
          <div className="sm:hidden space-y-2 mb-6">
            {(invoice.items || []).map((item, i) => {
              const qty = Number(item.quantity) || 0;
              const rate = Number(item.unit_price) || 0;
              const base = qty * rate;
              const itemDiscount = Number(item.discount_amount ?? 0) || 0;
              const isSvc = (item.kind || "product") === "service";
              return (
                <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-slate-400">#{i + 1}</div>
                      <div className="font-medium text-slate-900 truncate">{item.name}</div>
                      {item.hsn_code && <div className="text-[11px] text-slate-500 font-mono mt-0.5">{isSvc ? "SAC" : "HSN"}: {item.hsn_code}</div>}
                      {itemDiscount > 0.01 && <div className="text-[10px] text-red-500 mt-0.5">Original {fmt(base)} · Discount -{fmt(itemDiscount)}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-semibold text-slate-900">{fmt(item.total)}</div>
                    </div>
                  </div>
                  <div className={`grid ${servicesOnly ? "grid-cols-2" : "grid-cols-3"} gap-2 text-[11px] text-slate-500`}>
                    {!servicesOnly && (
                      <div>Qty: <span className="text-slate-700 font-medium">{isSvc ? "—" : `${qty}${item.unit ? ` ${item.unit}` : ""}`}</span></div>
                    )}
                    <div>{servicesOnly ? "Amount" : "Rate"}: <span className="text-slate-700 font-medium">{fmt(rate)}</span></div>
                    <div>GST: <span className="text-slate-700 font-medium">{item.gst_percent || 0}%</span></div>
                  </div>
                </div>
              );
            })}
          </div>
            </>
            );
          })()}

          {/* ===== Summary (Standardised Order) =====
              Subtotal (raw) → Discount → Taxable Value → CGST/SGST OR IGST → Non-GST → Shipping → Previous Balance → Round Off → Grand Total */}
          <div className="flex justify-end">
            <div className="w-full sm:w-80 space-y-1.5 text-sm" data-testid="invoice-summary">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal (Excl. Tax)</span><span>{fmt(invoice.raw_subtotal ?? invoice.subtotal)}</span></div>
              {(() => {
                // Sum per-item line discounts (when line_discount > 0 on any item)
                const lineDiscTotal = (invoice.items || []).reduce((s, it) => s + (Number(it.line_discount) || 0), 0);
                const overallDisc = Number(invoice.discount || 0);
                return (
                  <>
                    {lineDiscTotal > 0.01 && (
                      <div className="flex justify-between text-red-600"><span>Line-item Discounts</span><span data-testid="summary-line-discount">-{fmt(lineDiscTotal)}</span></div>
                    )}
                    {overallDisc > 0 && (
                      <div className="flex justify-between text-red-600"><span>Overall Discount</span><span data-testid="summary-discount">-{fmt(overallDisc)}</span></div>
                    )}
                    {(lineDiscTotal > 0.01 || overallDisc > 0) && (
                      <div className="flex justify-between border-t border-dashed border-slate-100 pt-1.5"><span className="text-slate-500 font-medium">Taxable Value</span><span className="font-medium">{fmt(invoice.subtotal)}</span></div>
                    )}
                  </>
                );
              })()}
              {(() => {
                const taxTotal = Number(invoice.tax_amount || 0);
                let cgstV = Number(invoice.cgst || 0);
                let sgstV = Number(invoice.sgst || 0);
                let igstV = Number(invoice.igst || 0);
                // Fallback for legacy invoices stored with 0 split
                if (invoice.state_type === "intra" && cgstV + sgstV < 0.01 && taxTotal > 0.01) {
                  cgstV = Math.round(taxTotal / 2 * 100) / 100;
                  sgstV = Math.round(taxTotal / 2 * 100) / 100;
                }
                if (invoice.state_type !== "intra" && igstV < 0.01 && taxTotal > 0.01) {
                  igstV = taxTotal;
                }
                return invoice.state_type === "intra" ? (
                  <>
                    <div className="flex justify-between"><span className="text-slate-500">CGST</span><span data-testid="summary-cgst">{fmt(cgstV)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">SGST</span><span data-testid="summary-sgst">{fmt(sgstV)}</span></div>
                  </>
                ) : (
                  <div className="flex justify-between"><span className="text-slate-500">IGST</span><span data-testid="summary-igst">{fmt(igstV)}</span></div>
                );
              })()}
              {(invoice.non_gst_total || 0) > 0 && (
                <div className="flex justify-between"><span className="text-amber-700">Non-GST / Reimbursements</span><span className="text-amber-700">+{fmt(invoice.non_gst_total)}</span></div>
              )}
              {(invoice.shipping || 0) > 0 && <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span>+{fmt(invoice.shipping)}</span></div>}
              {(invoice.previous_balance || 0) > 0 && (
                <div className="flex justify-between"><span className="text-slate-500">Previous Balance</span><span data-testid="summary-previous-balance">+{fmt(invoice.previous_balance)}</span></div>
              )}
              {(invoice.round_off || 0) !== 0 && <div className="flex justify-between"><span className="text-slate-500">Round Off</span><span>{fmt(invoice.round_off)}</span></div>}
              <div className="flex justify-between pt-2 border-t-2 border-slate-300 font-bold text-lg" data-testid="summary-grand-total">
                <span>Grand Total</span><span className="text-blue-600">{fmt(invoice.grand_total)}</span>
              </div>
              {invoice.tds_amount > 0 && <div className="flex justify-between"><span className="text-slate-500">TDS ({invoice.tds_section} @ {invoice.tds_rate}%)</span><span className="text-red-500">-{fmt(invoice.tds_amount)}</span></div>}
              {invoice.tcs_amount > 0 && <div className="flex justify-between"><span className="text-slate-500">TCS @ {invoice.tcs_rate}%</span><span className="text-emerald-600">+{fmt(invoice.tcs_amount)}</span></div>}
              {(invoice.tds_amount > 0 || invoice.tcs_amount > 0) && (
                <div className="flex justify-between pt-2 border-t border-slate-200 font-bold text-blue-600 text-lg">
                  <span>Net Payable</span><span>{fmt(invoice.net_payable || (invoice.grand_total - (invoice.tds_amount || 0) + (invoice.tcs_amount || 0)))}</span>
                </div>
              )}
              {totalPaid > 0 && (
                <>
                  <div className="flex justify-between text-emerald-600"><span>Paid</span><span>-{fmt(totalPaid)}</span></div>
                  <div className="flex justify-between font-bold"><span>Remaining</span><span className={remaining <= 0 ? "text-emerald-600" : "text-red-600"}>{fmt(remaining)}</span></div>
                </>
              )}
            </div>
          </div>

          {company?.signature_url && (
            <div className="mt-8 flex flex-col items-end" data-testid="signature-block">
              <img
                src={company.signature_url.startsWith("http") ? company.signature_url : `${process.env.REACT_APP_BACKEND_URL}${company.signature_url}`}
                alt="Authorized signatory"
                className="h-16 w-auto max-w-[180px] object-contain"
                data-testid="company-signature"
              />
              <p className="text-xs text-slate-500 mt-1 border-t border-slate-200 pt-1 min-w-[160px] text-center">Authorized Signatory</p>
            </div>
          )}

          {company?.bank_account_number && (
            <div className="mt-8 p-4 bg-slate-50 rounded-xl text-xs text-slate-600">
              <p className="font-semibold text-slate-700 mb-1">Bank Details</p>
              <p>{company.bank_name} | A/C: {company.bank_account_number} | IFSC: {company.bank_ifsc}</p>
              {company.upi_id && <p>UPI: {company.upi_id}</p>}
            </div>
          )}
        </div>

        {/* Payment Tracking */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid="payment-section">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-emerald-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Payments</h2>
            </div>
            {invoice.payment_status !== "paid" && (
              <Button onClick={() => setShowPaymentForm(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm" data-testid="record-payment-btn">
                <Plus className="w-4 h-4 mr-1" /> Record Payment
              </Button>
            )}
          </div>

          {showPaymentForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mb-5 p-4 bg-slate-50 rounded-xl border border-slate-100"
            >
              <div className="grid sm:grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Amount *</label>
                  <Input type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} placeholder={`Max: ${fmt(remaining)}`} data-testid="payment-amount" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Date</label>
                  <Input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} data-testid="payment-date" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Mode</label>
                  <Input value={paymentMode} onChange={e => setPaymentMode(e.target.value)} placeholder="UPI/Bank/Cash" data-testid="payment-mode" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleAddPayment} disabled={savingPayment} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm" data-testid="save-payment-btn">
                  {savingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Save
                </Button>
                <Button variant="ghost" onClick={() => setShowPaymentForm(false)} className="text-sm">Cancel</Button>
              </div>
            </motion.div>
          )}

          {(invoice.payments || []).length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No payments recorded yet</p>
          ) : (
            <div className="space-y-2">
              {(invoice.payments || []).map(p => (
                <div key={p.payment_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-testid={`payment-${p.payment_id}`}>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{fmt(p.amount)}</p>
                      <p className="text-xs text-slate-500">{p.payment_date} {p.payment_mode && `via ${p.payment_mode}`}</p>
                    </div>
                  </div>
                  <button onClick={() => handleDeletePayment(p.payment_id)} className="text-slate-300 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Evidence / Attachments */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_4px_20px_rgb(0,0,0,0.03)] p-5" data-testid="evidence-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-[#111827] flex items-center gap-2">
              <Paperclip className="w-4 h-4 text-[#6B7280]" /> Evidence & Attachments
            </h3>
            <label className="cursor-pointer">
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={handleEvidenceUpload} data-testid="evidence-file-input" />
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#2563EB] bg-[#EFF6FF] rounded-lg hover:bg-[#DBEAFE] transition-colors">
                {uploadingEvidence ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Upload
              </span>
            </label>
          </div>

          {evidence.length === 0 ? (
            <p className="text-sm text-[#9CA3AF] text-center py-4" data-testid="no-evidence">No evidence files attached yet</p>
          ) : (
            <div className="space-y-2">
              {evidence.map(ev => (
                <div key={ev.evidence_id} className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-xl" data-testid={`evidence-${ev.evidence_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <File className="w-5 h-5 text-[#6B7280] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#111827] truncate">{ev.original_filename}</p>
                      <p className="text-xs text-[#9CA3AF]">{(ev.file_size / 1024).toFixed(0)} KB &middot; {ev.created_at?.split("T")[0]}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {ev.file_type?.startsWith("image/") && (
                      <button onClick={() => setPreviewUrl(ev.file_url)} className="p-1.5 text-[#6B7280] hover:text-[#2563EB]" title="Preview">
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    <a href={ev.file_url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-[#6B7280] hover:text-[#2563EB]" title="Download">
                      <Download className="w-4 h-4" />
                    </a>
                    <button onClick={() => handleDeleteEvidence(ev.evidence_id)} className="p-1.5 text-[#9CA3AF] hover:text-[#EF4444]" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Audit Trail / Edit History */}
        {(invoice.edit_history || []).length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mt-4" data-testid="audit-trail-section">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-slate-500" />
              <h3 className="text-base font-semibold text-slate-900">Edit History</h3>
              <span className="text-xs text-slate-400 ml-auto">{invoice.edit_history.length} edit{invoice.edit_history.length > 1 ? "s" : ""}</span>
            </div>
            <div className="space-y-3">
              {[...invoice.edit_history].reverse().map((entry, idx) => (
                <div key={idx} className="border-l-2 border-slate-200 pl-4 py-1" data-testid={`audit-entry-${idx}`}>
                  <div className="flex items-baseline justify-between gap-2 mb-1 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900">{entry.edited_by_name || "User"}</p>
                    <p className="text-xs text-slate-400 font-mono">
                      {new Date(entry.edited_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                  <ul className="space-y-1 text-xs text-slate-600">
                    {(entry.changes || []).map((c, j) => (
                      <li key={j} className="flex items-baseline gap-1.5 flex-wrap">
                        <span className="font-mono text-slate-500 capitalize">{c.field.replace(/_/g, " ")}:</span>
                        <span className="line-through text-slate-400 break-all">{c.old_value || "—"}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-emerald-700 font-medium break-all">{c.new_value || "—"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-3xl max-h-[80vh]" onClick={e => e.stopPropagation()}>
            <button onClick={() => setPreviewUrl(null)} className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow flex items-center justify-center text-[#6B7280] hover:text-[#111827]">
              <X className="w-5 h-5" />
            </button>
            <img src={previewUrl} alt="Evidence preview" className="rounded-xl max-h-[75vh] object-contain" />
          </div>
        </div>
      )}

      {showCreditNoteDialog && (
        <CreditNoteDialog
          invoiceId={invoiceId}
          invoice={invoice}
          onClose={() => setShowCreditNoteDialog(false)}
          onCreated={handleCreditNoteCreated}
        />
      )}
    </div>
  );
}
